export type Lang = 'en' | 'es' | 'fr' | 'de' | 'pt' | 'it' | 'ja' | 'ko' | 'zh' | 'nl' | 'ru' | 'pl' | 'sv' | 'tr' | 'ar' | 'no';

export const LANGUAGE_NAMES: Record<Lang, string> = {
  en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch',
  pt: 'Português', it: 'Italiano', ja: '日本語', ko: '한국어',
  zh: '中文', nl: 'Nederlands', ru: 'Русский', pl: 'Polski',
  sv: 'Svenska', tr: 'Türkçe', ar: 'العربية', no: 'Norsk',
};

type Strings = Record<string, string>;

const en: Strings = {
  unassigned: 'Unassigned', assigned: 'Assigned', no_room_joined: 'No room joined',
  repair_title: 'Repair this room (+1%)', repair_disabled: 'Room repairs are available once the mission starts.',
  mission_running: 'Mission running', lobby: 'Lobby',
  room_help: 'Room Help', room_help_subtitle: 'What this room controls',
  room_help_body: 'Select a room to see a description of its controls.',
  room_help_default: 'This room provides controls for the current role.',
  room_help_select: 'Select a room to see what it controls.',
  no_room: 'No room', select_room: 'Select a room.',
  settings_title: 'Room Settings', close: 'Close',
  settings_graphics: 'Graphics', settings_antialiasing: 'Antialiasing',
  settings_vibration: 'Vibration', settings_audio: 'Device audio', settings_language: 'Language',
  settings_audio_section: 'Audio adjustments', settings_tv_music: 'TV music', settings_tv_effects: 'TV effects', settings_phone_volume: 'This phone',
  settings_audio_hint: 'TV volumes apply to the shared screen for the whole crew.', settings_audio_need_room: 'Join a room to change the TV volumes.',
  quality_low: 'Low', quality_medium: 'Medium', quality_high: 'High', quality_ultra: 'Ultra',
  on: 'On', off: 'Off', vibration_high: 'High', vibration_low: 'Low',
  rooms_title: 'Rooms',
  role_navigation: 'Navigation', role_captain: 'Captain', role_docking: 'Docking',
  role_pilot: 'Pilot', role_ballast: 'Ballast', role_sonar: 'Sonar',
  role_mining: 'Mining', role_battery: 'Battery', role_storage: 'Storage & Processing',
  role_life_support: 'Life Support', role_drone: 'Drone Control', role_engineer: 'Engineering',
  role_engine: 'Engine', role_periscope: 'Periscope', role_quarters: 'Crew Quarters',
  help_quarters: 'Bunks and lockers for the crew. Nothing here needs operating.' + '\n\n' + 'The room exists as ballast: when Engineering floods it the whole boat gets heavier and sinks fast. You are evacuated first.',
  help_navigation: 'Use the map to see revealed terrain and plan your route. Drag to pan, pinch or scroll to zoom.\n\nRemember to regularly ping the sonar to reveal more of the map and identify resource locations.\n\nThe map also shows hazards and points of interest — keep an eye on it to navigate safely and efficiently.',
  help_captain: 'The captain\'s bridge provides a full picture of the mission and the submarine\'s status.\n\nTrack active mission objectives on the Mission tab. The Submarine tab shows heading, depth, speed, and fuel. The Crew tab lets you monitor and manage who is in which room. The Status tab shows hull zone integrity across the boat.\n\nThe captain also controls the forward lights — a powerful floodlight that helps the pilot and periscope crew see what\'s ahead.',
  help_docking: 'Handles docking procedures when the submarine needs to attach to a station, facility, or recoverable object.\n\nThis is the only crew position with a downward-facing view of the submarine\'s underside — essential for lining up precisely during approach.\n\nCoordinate closely with the pilot to ensure a safe and accurate dock.',
  help_pilot: 'Controls fine maneuvering and heading while underway.\n\nWork closely with the ballast crew to maintain safe ascent and descent profiles, and with the sonar crew to navigate around hazards and identify resource locations.\n\nBe mindful of speed and heading to avoid collisions — impacts cause significant hull damage.\n\nHigher thruster output consumes more power, so coordinate with the battery crew to ensure sufficient energy for maneuvers.',
  help_ballast: 'Manages the fore and aft ballast tanks to control buoyancy and trim.\n\nAdjusting ballast is a slow process — plan maneuvers ahead of time and communicate with the pilot. Differential ballast (more weight fore or aft) will also pitch the boat, which affects how the thrusters drive it.\n\nFor safe and efficient movement through the underwater environment, anticipate depth changes early.',
  help_sonar: 'Use the ping button to send a sonar pulse in all directions that will bounce off any surface and return an image of the surrounding terrain.\n\nThe sonar will also return approximate locations of resources deep within the rock surface.\n\nAdjust the range to change the strength of the pulse — be careful, as longer-range pulses draw significantly more power!',
  help_mining: 'Operates the mining laser for extracting resources from the seabed.\n\nUse Scan to detect nearby resource nodes and get a targeting bearing. Fire the laser to continuously extract from any rock it hits — resources yield every few seconds until the node is depleted.\n\nThe mining bay also has an external light to help spot targets in dark water.',
  help_battery: 'Monitors the submarine\'s power reserves and manages how power is distributed across all rooms.\n\nUse the per-room power controls to cut power to non-essential systems when reserves are low. Auto-off thresholds let a room power down and back up automatically as battery level fluctuates.\n\nThe overcharge function temporarily boosts battery charge rate — useful in an emergency, but hold it too long and the hull will take heat damage.',
  help_storage: 'Manages shared cargo and the onboard material processor.\n\nRaw materials collected by the harpoon or drones are stored here. The processor can refine raw materials into more useful products — check the active mission objectives to know what is needed.\n\nKeep an eye on inventory to ensure the crew has the supplies they need.',
  help_life_support: 'Controls oxygen generation and cabin temperature to keep the crew alive.\n\nGenerating oxygen and running the heater both draw power — balance the needs of the crew against available energy. The heater warms the cabin to at most 30 degrees above the outside water temperature and cuts out automatically above 20 degrees.\n\nAt great depth the surrounding water is very cold, so the heater will be needed more often.',
  help_drone: 'Drone operations bay — coming soon.\n\nThis room will allow the crew to deploy and pilot autonomous drones for scouting, collection, and repair tasks.',
  help_engineer: 'Monitors hull integrity and manages flooding across the submarine.\n\nThe engineer\'s panel shows the four hull zones (fore, mid-fore, mid-aft, aft), their current integrity, and which rooms are flooding. When a zone is breached, the engineer assigns bilge pumps to flooded rooms to slow the ingress.\n\nPatching a breached zone requires hull patch materials from storage. Coordinate with the storage crew to ensure patches are available before a situation becomes critical.',
  help_engine: 'Controls the submarine\'s engines and manages fuel.\n\nThe engine output determines how much power is available to all other rooms. Running at high output burns fuel faster — balance output against demand and watch the fuel gauges closely.\n\nDifferent fuel types have different burn rates and power profiles. Constant adjustments are required to make efficient use of precious fuel resources.',
  help_periscope: 'Use the periscope to survey the submarine\'s surroundings with a magnified exterior view.\n\nThe periscope is equipped with a powerful spotlight. Adjust the focus to identify specific items or spread wide for general illumination.\n\nThe spotlight draws a lot of power — turn it off when not needed to conserve energy.',
  nav_heading: 'Navigation', nav_description: 'Node map. Drag to pan, pinch or scroll to zoom.',
  nav_set_waypoint: 'Set Waypoint', nav_clear_waypoint: 'Clear Waypoint',
  coming_soon: 'Coming soon', all_stop: 'ALL STOP', dive: '▼ DIVE', rise: '▲ RISE',
  fire_emp: 'FIRE EMP', save_game: 'Save game',
  mining_laser_fire: 'FIRE LASER', mining_laser_stop: 'STOP LASER', mining_laser_cooling: 'COOLING...',
  mining_laser_armed: 'Laser active. Stop to deactivate.', mining_laser_ready: 'Laser ready.',
  scanning: 'Scanning...',
  reset_engine: 'Reset engine', change_fuel: 'Change fuel', restore_power: 'Restore Power',
  unassign: 'Unassign', kick: 'Kick', confirm: 'Confirm?',
  no_missions: 'No missions loaded.', no_crew: 'No crew connected.',
  quick_filter: 'Quick filter', search: '⌕ Search', crafting: 'Crafting',
  pump_status: 'PUMP STATUS', current_power_drains: 'Current power drains',
  fuel_loaded: 'Fuel loaded.', no_fuel_stopped: 'NO FUEL — ENGINE STOPPED',
  engine_reset: 'Reset engine', lox_boost_off: 'LOX boost: OFF',
  throttle: 'throttle', actual: 'ACTUAL', target: 'TARGET',
  battery_warning: 'Warning battery level too low for configured ping!',
  select_fuel: 'Select a stocked canister from Storage & Processing.',
  change_fuel_heading: 'Change Fuel', changing_fuel: 'Changing Fuel', close_btn: 'Close',
  room_power: 'Room power', auto_off: 'Auto-off: OFF',
};

const es: Strings = {
  unassigned: 'Sin asignar', assigned: 'Asignado', no_room_joined: 'Sin sala',
  repair_title: 'Reparar sala (+1%)', repair_disabled: 'Reparaciones disponibles al iniciar la misión.',
  mission_running: 'Misión en curso', lobby: 'Sala de espera',
  room_help: 'Ayuda de sala', room_help_subtitle: 'Lo que controla esta sala',
  room_help_body: 'Selecciona una sala para ver su descripción.',
  room_help_default: 'Esta sala proporciona controles para el rol actual.',
  room_help_select: 'Selecciona una sala para ver sus controles.',
  no_room: 'Sin sala', select_room: 'Seleccionar sala.',
  settings_title: 'Configuración', close: 'Cerrar',
  settings_graphics: 'Gráficos', settings_antialiasing: 'Antialiasing',
  settings_vibration: 'Vibración', settings_audio: 'Audio del dispositivo', settings_language: 'Idioma',
  quality_low: 'Bajo', quality_medium: 'Medio', quality_high: 'Alto', quality_ultra: 'Ultra',
  on: 'Activado', off: 'Desactivado', vibration_high: 'Alto', vibration_low: 'Bajo',
  rooms_title: 'Salas',
  role_navigation: 'Navegación', role_captain: 'Capitán', role_docking: 'Atraque',
  role_pilot: 'Piloto', role_ballast: 'Lastre', role_sonar: 'Sonar',
  role_mining: 'Arpón', role_battery: 'Batería', role_storage: 'Almacenamiento',
  role_life_support: 'Soporte vital', role_drone: 'Drones', role_engineer: 'Ingeniería',
  role_engine: 'Motor', role_periscope: 'Periscopio',
  help_navigation: 'Usa el mapa para ver el terreno revelado y trazar tu ruta.\n\n¡Haz ping al sonar regularmente para revelar más del mapa!\n\nEl mapa también muestra peligros y puntos de interés.',
  help_captain: 'Supervisa los objetivos de la misión, las comunicaciones y la coordinación del equipo.',
  help_docking: 'Gestiona los procedimientos de atraque.\n\nEs la única posición con vista hacia abajo del submarino, crucial para un atraque preciso.',
  help_pilot: 'Controla las maniobras finas y el rumbo.\n\nTrabaja con el lastre y el sonar para navegación segura.\n\nControla la velocidad para evitar colisiones.\n\nMás velocidad consume más energía; coordina con la batería.',
  help_ballast: 'Gestiona los tanques de lastre y la flotabilidad.\n\nAjustar el lastre es lento: planifica con anticipación y comunícate con el piloto.',
  help_sonar: 'Envía pulsos sonar en todas las direcciones.\n\nEl sonar también localiza recursos.\n\n¡Los pulsos más fuertes consumen mucha más energía!',
  help_mining: 'Opera el arpón para recoger recursos. Dispara, escanea y recupera.',
  help_battery: 'Monitorea y restaura la energía.\n\n¡Usa la sobrecarga para aumentar temporalmente la carga con riesgo de dañar el casco!',
  help_storage: 'Organiza el inventario y procesa materiales para la misión.',
  help_life_support: 'Controla el oxígeno y la temperatura.\n\nEquilibra las necesidades de la tripulación con la energía disponible.',
  help_drone: 'Bahía de drones. Próximamente.',
  help_engineer: 'Gestiona diagnósticos y reparaciones en el submarino.',
  help_engine: 'Controla la potencia del motor.\n\n¡Se requieren ajustes constantes para usar el combustible eficientemente!',
  help_periscope: 'Usa el periscopio para inspeccionar los alrededores.\n\nAjusta el foco del faro según necesites.\n\nEl foco consume mucha energía; apágalo cuando no sea necesario.',
  nav_heading: 'Navegación', nav_description: 'Mapa de nodos. Arrastra, pellizca o desplaza para zoom.',
  nav_set_waypoint: 'Establecer punto', nav_clear_waypoint: 'Borrar punto',
  coming_soon: 'Próximamente', all_stop: 'PARADA TOTAL', dive: '▼ BUCEAR', rise: '▲ SUBIR',
  fire_emp: 'DISPARAR EMP', save_game: 'Guardar partida',
  mining_laser_fire: 'DISPARAR', mining_laser_stop: 'RECUPERAR', mining_laser_cooling: 'RECARGANDO...',
  mining_laser_armed: 'Arpón armado. Recuperar para recargar.', mining_laser_ready: 'Arpón listo.',
  scanning: 'Escaneando...',
  reset_engine: 'Reiniciar motor', change_fuel: 'Cambiar combustible', restore_power: 'Restaurar energía',
  unassign: 'Desasignar', kick: 'Expulsar', confirm: '¿Confirmar?',
  no_missions: 'No hay misiones cargadas.', no_crew: 'Sin tripulación conectada.',
  quick_filter: 'Filtro rápido', search: '⌕ Buscar', crafting: 'Fabricación',
  pump_status: 'ESTADO DE BOMBAS', current_power_drains: 'Consumos de energía actuales',
  fuel_loaded: 'Combustible cargado.', no_fuel_stopped: 'SIN COMBUSTIBLE — MOTOR DETENIDO',
  engine_reset: 'Reiniciar motor', lox_boost_off: 'Impulso LOX: APAGADO',
  throttle: 'acelerador', actual: 'REAL', target: 'OBJETIVO',
  battery_warning: '¡Advertencia: batería demasiado baja para el ping configurado!',
  select_fuel: 'Selecciona un bote del Almacenamiento y Procesamiento.',
  change_fuel_heading: 'Cambiar Combustible', changing_fuel: 'Cambiando Combustible', close_btn: 'Cerrar',
  room_power: 'Energía de sala', auto_off: 'Apagado auto: DESACTIVADO',
};

const fr: Strings = {
  unassigned: 'Non assigné', assigned: 'Assigné', no_room_joined: 'Aucune salle',
  repair_title: 'Réparer la salle (+1%)', repair_disabled: 'Réparations disponibles au démarrage de la mission.',
  mission_running: 'Mission en cours', lobby: "Salle d'attente",
  room_help: 'Aide de la salle', room_help_subtitle: 'Ce que contrôle cette salle',
  room_help_body: 'Sélectionnez une salle pour voir sa description.',
  room_help_default: 'Cette salle fournit des commandes pour le rôle actuel.',
  room_help_select: 'Sélectionnez une salle pour voir ses commandes.',
  no_room: 'Aucune salle', select_room: 'Sélectionner une salle.',
  settings_title: 'Paramètres', close: 'Fermer',
  settings_graphics: 'Graphismes', settings_antialiasing: 'Anticrénelage',
  settings_vibration: 'Vibration', settings_audio: 'Audio appareil', settings_language: 'Langue',
  quality_low: 'Faible', quality_medium: 'Moyen', quality_high: 'Élevé', quality_ultra: 'Ultra',
  on: 'Activé', off: 'Désactivé', vibration_high: 'Élevée', vibration_low: 'Faible',
  rooms_title: 'Salles',
  role_navigation: 'Navigation', role_captain: 'Capitaine', role_docking: 'Amarrage',
  role_pilot: 'Pilote', role_ballast: 'Ballast', role_sonar: 'Sonar',
  role_mining: 'Harpon', role_battery: 'Batterie', role_storage: 'Stockage & Traitement',
  role_life_support: 'Support vie', role_drone: 'Drones', role_engineer: 'Ingénierie',
  role_engine: 'Moteur', role_periscope: 'Périscope',
  help_navigation: "Utilisez la carte pour voir le terrain révélé et tracer votre route.\n\nPinguez régulièrement le sonar pour révéler la carte !\n\nLa carte affiche aussi les dangers et points d'intérêt.",
  help_captain: "Supervise les objectifs de mission, les communications et la coordination de l'équipe.",
  help_docking: "Gère les procédures d'amarrage.\n\nC'est la seule position avec vue sous le sous-marin, essentielle pour un amarrage précis.",
  help_pilot: "Contrôle les manœuvres fines et le cap.\n\nTravaillez avec le ballast et le sonar.\n\nSoyez attentif à votre vitesse pour éviter les collisions.\n\nLes vitesses élevées consomment plus d'énergie.",
  help_ballast: "Gère les réservoirs de ballast.\n\nAjuster le ballast est lent — planifiez à l'avance et communiquez avec le pilote.",
  help_sonar: "Envoyez des impulsions sonar dans toutes les directions.\n\nLe sonar renvoie aussi des localisations de ressources.\n\nLes impulsions plus fortes consomment beaucoup plus d'énergie !",
  help_mining: 'Utilisez le harpon pour collecter des ressources. Tirez, scannez et récupérez.',
  help_battery: "Surveille et restaure les réserves d'énergie.\n\nUtilisez la surcharge pour augmenter temporairement la charge avec risque pour la coque !",
  help_storage: "Organise l'inventaire et traite les matériaux pour la mission.",
  help_life_support: "Contrôle la génération d'oxygène et la température.\n\nÉquilibrez les besoins de l'équipage avec l'énergie disponible.",
  help_drone: 'Baie des drones. Bientôt disponible.',
  help_engineer: 'Gère les diagnostics système et les réparations.',
  help_engine: "Contrôle la puissance du moteur pour les sous-systèmes.\n\nDes ajustements constants sont nécessaires pour utiliser efficacement le carburant !",
  help_periscope: "Utilisez le périscope pour inspecter les environs.\n\nÉquipé d'un projecteur puissant.\n\nLe projecteur consomme beaucoup d'énergie ; éteignez-le si non nécessaire.",
  nav_heading: 'Navigation', nav_description: 'Carte des nœuds. Faites glisser, pincez ou défilez.',
  nav_set_waypoint: 'Définir point', nav_clear_waypoint: 'Effacer point',
  coming_soon: 'Bientôt', all_stop: 'ARRÊT TOTAL', dive: '▼ PLONGER', rise: '▲ MONTER',
  fire_emp: 'TIRER EMP', save_game: 'Sauvegarder',
  mining_laser_fire: 'TIRER', mining_laser_stop: 'RÉCUPÉRER', mining_laser_cooling: 'RECHARGEMENT...',
  mining_laser_armed: 'Harpon armé. Récupérer pour recharger.', mining_laser_ready: 'Harpon prêt.',
  scanning: 'Scan en cours...',
  reset_engine: 'Réinitialiser moteur', change_fuel: 'Changer carburant', restore_power: 'Restaurer énergie',
  unassign: 'Désassigner', kick: 'Expulser', confirm: 'Confirmer ?',
  no_missions: 'Aucune mission chargée.', no_crew: 'Aucun équipage connecté.',
  quick_filter: 'Filtre rapide', search: '⌕ Rechercher', crafting: 'Fabrication',
  pump_status: 'ÉTAT DES POMPES', current_power_drains: 'Consommations actuelles',
  fuel_loaded: 'Carburant chargé.', no_fuel_stopped: 'SANS CARBURANT — MOTEUR ARRÊTÉ',
  engine_reset: 'Réinitialiser moteur', lox_boost_off: 'Boost LOX : DÉSACTIVÉ',
  throttle: 'accélérateur', actual: 'RÉEL', target: 'CIBLE',
  battery_warning: 'Attention : niveau de batterie trop bas pour le ping configuré !',
  select_fuel: 'Sélectionnez une bonbonne du Stockage & Traitement.',
  change_fuel_heading: 'Changer Carburant', changing_fuel: 'Changement en cours', close_btn: 'Fermer',
  room_power: 'Alimentation salle', auto_off: 'Auto-arrêt : DÉSACTIVÉ',
};

const de: Strings = {
  unassigned: 'Nicht zugewiesen', assigned: 'Zugewiesen', no_room_joined: 'Kein Raum',
  repair_title: 'Raum reparieren (+1%)', repair_disabled: 'Reparaturen verfügbar wenn Mission startet.',
  mission_running: 'Mission läuft', lobby: 'Lobby',
  room_help: 'Raumhilfe', room_help_subtitle: 'Was dieser Raum steuert',
  room_help_body: 'Raum wählen für Beschreibung.',
  room_help_default: 'Dieser Raum bietet Steuerungen für die aktuelle Rolle.',
  room_help_select: 'Raum wählen für Steuerungen.',
  no_room: 'Kein Raum', select_room: 'Raum auswählen.',
  settings_title: 'Einstellungen', close: 'Schließen',
  settings_graphics: 'Grafik', settings_antialiasing: 'Kantenglättung',
  settings_vibration: 'Vibration', settings_audio: 'Geräteton', settings_language: 'Sprache',
  quality_low: 'Niedrig', quality_medium: 'Mittel', quality_high: 'Hoch', quality_ultra: 'Ultra',
  on: 'Ein', off: 'Aus', vibration_high: 'Hoch', vibration_low: 'Niedrig',
  rooms_title: 'Räume',
  role_navigation: 'Navigation', role_captain: 'Kapitän', role_docking: 'Andocken',
  role_pilot: 'Pilot', role_ballast: 'Ballast', role_sonar: 'Sonar',
  role_mining: 'Harpune', role_battery: 'Batterie', role_storage: 'Lager & Verarbeitung',
  role_life_support: 'Lebenserhaltung', role_drone: 'Drohnen', role_engineer: 'Technik',
  role_engine: 'Maschine', role_periscope: 'Periskop',
  help_navigation: 'Karte nutzen um Terrain zu sehen und Route zu planen.\n\nSonar regelmäßig pingen!\n\nKarte zeigt Gefahren und Interessenpunkte.',
  help_captain: 'Überwacht Missionsziele, Kommunikation und Teamkoordination.',
  help_docking: 'Andockverfahren verwalten.\n\nEinzige Position mit Sicht nach unten — entscheidend für präzises Andocken.',
  help_pilot: 'Steuert Feinmanöver und Kurs.\n\nEng mit Ballast und Sonar arbeiten.\n\nHohe Geschwindigkeiten verbrauchen mehr Energie.',
  help_ballast: 'Ballasttanks und Auftrieb verwalten.\n\nBallastanpassungen dauern lange — vorausplanen.',
  help_sonar: 'Sonar-Pulse senden.\n\nStandorte von Ressourcen erkennen.\n\nStärkere Pulse verbrauchen viel mehr Energie!',
  help_mining: 'Harpune bedienen. Feuern, Scannen, Einziehen.',
  help_battery: 'Energiereserven überwachen.\n\nÜberladung für temporären Boost — Rumpfrisiko!',
  help_storage: 'Inventar organisieren und Materialien verarbeiten.',
  help_life_support: 'Sauerstoff und Kabinentemperatur steuern.\n\nBesatzungsbedarf gegen Energie abwägen.',
  help_drone: 'Drohnenbetriebsraum. Demnächst.',
  help_engineer: 'Systemdiagnosen und Reparaturen verwalten.',
  help_engine: 'Motorleistung steuern.\n\nKonstante Anpassungen für effizienten Treibstoffeinsatz!',
  help_periscope: 'Periskop nutzen.\n\nLeistungsstarker Scheinwerfer — Fokus anpassen.\n\nViel Energieverbrauch; ausschalten wenn unnötig.',
  nav_heading: 'Navigation', nav_description: 'Knotenkarte. Ziehen, Kneifen oder Scrollen.',
  nav_set_waypoint: 'Wegpunkt setzen', nav_clear_waypoint: 'Wegpunkt löschen',
  coming_soon: 'Demnächst', all_stop: 'ALLE STOPP', dive: '▼ TAUCHEN', rise: '▲ AUFSTEIGEN',
  fire_emp: 'EMP FEUERN', save_game: 'Spiel speichern',
  mining_laser_fire: 'FEUERN', mining_laser_stop: 'EINZIEHEN', mining_laser_cooling: 'LÄDT...',
  mining_laser_armed: 'Harpune bereit. Einziehen zum Nachladen.', mining_laser_ready: 'Harpune geladen.',
  scanning: 'Scannt...',
  reset_engine: 'Motor zurücksetzen', change_fuel: 'Treibstoff wechseln', restore_power: 'Energie wiederherstellen',
  unassign: 'Aufheben', kick: 'Rauswerfen', confirm: 'Bestätigen?',
  no_missions: 'Keine Missionen geladen.', no_crew: 'Keine Besatzung verbunden.',
  quick_filter: 'Schnellfilter', search: '⌕ Suchen', crafting: 'Herstellung',
  pump_status: 'PUMPENSTATUS', current_power_drains: 'Aktuelle Verbraucher',
  fuel_loaded: 'Treibstoff geladen.', no_fuel_stopped: 'KEIN TREIBSTOFF — MOTOR GESTOPPT',
  engine_reset: 'Motor zurücksetzen', lox_boost_off: 'LOX-Boost: AUS',
  throttle: 'Drossel', actual: 'IST', target: 'SOLL',
  battery_warning: 'Warnung: Batteriestand zu niedrig für konfigurierten Ping!',
  select_fuel: 'Kanister aus Lager & Verarbeitung wählen.',
  change_fuel_heading: 'Treibstoff wechseln', changing_fuel: 'Wechsel läuft', close_btn: 'Schließen',
  room_power: 'Raumstrom', auto_off: 'Auto-Aus: AUS',
};

const pt: Strings = {
  unassigned: 'Não atribuído', assigned: 'Atribuído', no_room_joined: 'Nenhuma sala',
  repair_title: 'Reparar sala (+1%)', repair_disabled: 'Reparos disponíveis ao iniciar a missão.',
  mission_running: 'Missão ativa', lobby: 'Lobby',
  room_help: 'Ajuda da sala', room_help_subtitle: 'O que esta sala controla',
  room_help_body: 'Selecione uma sala para ver sua descrição.',
  room_help_default: 'Esta sala fornece controles para o papel atual.',
  room_help_select: 'Selecione uma sala para ver seus controles.',
  no_room: 'Sem sala', select_room: 'Selecionar sala.',
  settings_title: 'Configurações', close: 'Fechar',
  settings_graphics: 'Gráficos', settings_antialiasing: 'Antialiasing',
  settings_vibration: 'Vibração', settings_audio: 'Áudio do dispositivo', settings_language: 'Idioma',
  quality_low: 'Baixo', quality_medium: 'Médio', quality_high: 'Alto', quality_ultra: 'Ultra',
  on: 'Ligado', off: 'Desligado', vibration_high: 'Alto', vibration_low: 'Baixo',
  rooms_title: 'Salas',
  role_navigation: 'Navegação', role_captain: 'Capitão', role_docking: 'Atracação',
  role_pilot: 'Piloto', role_ballast: 'Lastro', role_sonar: 'Sonar',
  role_mining: 'Arpão', role_battery: 'Bateria', role_storage: 'Armazenamento',
  role_life_support: 'Suporte de vida', role_drone: 'Drones', role_engineer: 'Engenharia',
  role_engine: 'Motor', role_periscope: 'Periscópio',
  help_navigation: 'Use o mapa para ver o terreno revelado e planejar sua rota.\n\nPingue o sonar regularmente!\n\nO mapa também mostra perigos e pontos de interesse.',
  help_captain: 'Supervisiona objetivos de missão, comunicações e coordenação da equipe.',
  help_docking: 'Gerencia procedimentos de atracação.\n\nÚnica posição com visão abaixo do submarino — crucial para atracação precisa.',
  help_pilot: 'Controla manobras finas e direção.\n\nTrabalhe com o lastro e o sonar.\n\nVelocidades altas consomem mais energia.',
  help_ballast: 'Gerencia tanques de lastro.\n\nAjustar o lastro é lento — planeje com antecedência.',
  help_sonar: 'Envie pulsos de sonar.\n\nO sonar também localiza recursos.\n\nPulsos mais fortes consomem muito mais energia!',
  help_mining: 'Opera o arpão para coletar recursos. Dispare, escaneie e recupere.',
  help_battery: 'Monitora e restaura reservas de energia.\n\nUse a sobrecarga para aumentar temporariamente com risco ao casco!',
  help_storage: 'Organiza inventário e processa materiais.',
  help_life_support: 'Controla oxigênio e temperatura.\n\nEquilibre as necessidades da tripulação com a energia disponível.',
  help_drone: 'Baía de drones. Em breve.',
  help_engineer: 'Gerencia diagnósticos e reparos no submarino.',
  help_engine: 'Controla a potência do motor.\n\nAjustes constantes são necessários para usar o combustível eficientemente!',
  help_periscope: 'Use o periscópio para inspecionar os arredores.\n\nHolofote potente — ajuste o foco.\n\nO holofote consome muita energia; desligue quando não precisar.',
  nav_heading: 'Navegação', nav_description: 'Mapa de nós. Arraste, aperte ou role para zoom.',
  nav_set_waypoint: 'Definir waypoint', nav_clear_waypoint: 'Limpar waypoint',
  coming_soon: 'Em breve', all_stop: 'PARADA TOTAL', dive: '▼ MERGULHAR', rise: '▲ SUBIR',
  fire_emp: 'DISPARAR EMP', save_game: 'Salvar jogo',
  mining_laser_fire: 'DISPARAR', mining_laser_stop: 'RECUPERAR', mining_laser_cooling: 'RECARREGANDO...',
  mining_laser_armed: 'Arpão armado. Recuperar para recarregar.', mining_laser_ready: 'Arpão pronto.',
  scanning: 'Escaneando...',
  reset_engine: 'Reiniciar motor', change_fuel: 'Trocar combustível', restore_power: 'Restaurar energia',
  unassign: 'Desatribuir', kick: 'Expulsar', confirm: 'Confirmar?',
  no_missions: 'Nenhuma missão carregada.', no_crew: 'Nenhuma tripulação conectada.',
  quick_filter: 'Filtro rápido', search: '⌕ Buscar', crafting: 'Fabricação',
  pump_status: 'STATUS DAS BOMBAS', current_power_drains: 'Consumos de energia atuais',
  fuel_loaded: 'Combustível carregado.', no_fuel_stopped: 'SEM COMBUSTÍVEL — MOTOR PARADO',
  engine_reset: 'Reiniciar motor', lox_boost_off: 'Boost LOX: DESLIGADO',
  throttle: 'acelerador', actual: 'REAL', target: 'ALVO',
  battery_warning: 'Aviso: nível de bateria muito baixo para o ping configurado!',
  select_fuel: 'Selecione um cartucho do Armazenamento.',
  change_fuel_heading: 'Trocar Combustível', changing_fuel: 'Trocando Combustível', close_btn: 'Fechar',
  room_power: 'Energia da sala', auto_off: 'Auto-desligar: DESLIGADO',
};

const it: Strings = {
  unassigned: 'Non assegnato', assigned: 'Assegnato', no_room_joined: 'Nessuna stanza',
  repair_title: 'Ripara stanza (+1%)', repair_disabled: 'Riparazioni disponibili avviando la missione.',
  mission_running: 'Missione in corso', lobby: 'Atrio',
  room_help: 'Aiuto stanza', room_help_subtitle: 'Cosa controlla questa stanza',
  room_help_body: 'Seleziona una stanza per vedere la descrizione.',
  room_help_default: 'Questa stanza fornisce controlli per il ruolo attuale.',
  room_help_select: 'Seleziona una stanza per vedere i controlli.',
  no_room: 'Nessuna stanza', select_room: 'Seleziona stanza.',
  settings_title: 'Impostazioni', close: 'Chiudi',
  settings_graphics: 'Grafica', settings_antialiasing: 'Antialiasing',
  settings_vibration: 'Vibrazione', settings_audio: 'Audio dispositivo', settings_language: 'Lingua',
  quality_low: 'Bassa', quality_medium: 'Media', quality_high: 'Alta', quality_ultra: 'Ultra',
  on: 'Attivo', off: 'Disattivo', vibration_high: 'Alta', vibration_low: 'Bassa',
  rooms_title: 'Stanze',
  role_navigation: 'Navigazione', role_captain: 'Capitano', role_docking: 'Attracco',
  role_pilot: 'Pilota', role_ballast: 'Zavorra', role_sonar: 'Sonar',
  role_mining: 'Fiocina', role_battery: 'Batteria', role_storage: 'Stoccaggio',
  role_life_support: 'Supporto vitale', role_drone: 'Droni', role_engineer: 'Ingegneria',
  role_engine: 'Motore', role_periscope: 'Periscopio',
  help_navigation: "Usa la mappa per vedere il terreno rivelato e tracciare il percorso.\n\nRicorda di pingare il sonar regolarmente!\n\nLa mappa mostra anche pericoli e punti di interesse.",
  help_captain: 'Supervisiona gli obiettivi di missione, le comunicazioni e il coordinamento del team.',
  help_docking: "Gestisce le procedure di attracco.\n\nÈ l'unica posizione con vista sotto il sottomarino, cruciale per un attracco preciso.",
  help_pilot: 'Controlla le manovre fini e la rotta.\n\nLavora strettamente con zavorra e sonar.\n\nVelocità più alte consumano più energia.',
  help_ballast: 'Gestisce le cisterne di zavorra.\n\nRegolare la zavorra è lento — pianifica in anticipo.',
  help_sonar: 'Invia impulsi sonar.\n\nLocalizza anche risorse nel sottosuolo.\n\nImpulsi più forti consumano molta più energia!',
  help_mining: 'Opera il rampino per raccogliere risorse. Spara, scansiona e recupera.',
  help_battery: "Monitora e ripristina le riserve di energia.\n\nUsa il sovraccarico per aumentare temporaneamente la carica con rischio per lo scafo!",
  help_storage: "Organizza l'inventario e processa i materiali.",
  help_life_support: "Controlla la generazione di ossigeno e la temperatura.\n\nBilancia le esigenze dell'equipaggio con l'energia disponibile.",
  help_drone: 'Baia operativa droni. Prossimamente.',
  help_engineer: 'Gestisce diagnostica e riparazioni nel sottomarino.',
  help_engine: 'Controlla la potenza del motore.\n\nSono necessari aggiustamenti costanti per usare il carburante in modo efficiente!',
  help_periscope: 'Usa il periscopio per ispezionare i dintorni.\n\nFaro potente — regola la messa a fuoco.\n\nIl faro consuma molta energia; spegnilo quando non necessario.',
  nav_heading: 'Navigazione', nav_description: 'Mappa nodi. Trascina, pizzica o scorri.',
  nav_set_waypoint: 'Imposta waypoint', nav_clear_waypoint: 'Cancella waypoint',
  coming_soon: 'Prossimamente', all_stop: 'STOP TOTALE', dive: '▼ IMMERSIONE', rise: '▲ RISALITA',
  fire_emp: 'LANCIA EMP', save_game: 'Salva partita',
  mining_laser_fire: 'SPARA', mining_laser_stop: 'RECUPERA', mining_laser_cooling: 'RICARICA...',
  mining_laser_armed: 'Rampino armato. Recuperare per ricaricare.', mining_laser_ready: 'Rampino pronto.',
  scanning: 'Scansione...',
  reset_engine: 'Reimposta motore', change_fuel: 'Cambia carburante', restore_power: 'Ripristina energia',
  unassign: 'Deassegna', kick: 'Espelli', confirm: 'Confermare?',
  no_missions: 'Nessuna missione caricata.', no_crew: 'Nessun equipaggio connesso.',
  quick_filter: 'Filtro rapido', search: '⌕ Cerca', crafting: 'Costruzione',
  pump_status: 'STATO POMPE', current_power_drains: 'Consumi energetici attuali',
  fuel_loaded: 'Carburante caricato.', no_fuel_stopped: 'SENZA CARBURANTE — MOTORE FERMO',
  engine_reset: 'Reimposta motore', lox_boost_off: 'Boost LOX: SPENTO',
  throttle: 'acceleratore', actual: 'REALE', target: 'OBIETTIVO',
  battery_warning: 'Avviso: livello batteria troppo basso per il ping configurato!',
  select_fuel: 'Seleziona una bombola dallo Stoccaggio.',
  change_fuel_heading: 'Cambio Carburante', changing_fuel: 'Cambio in corso', close_btn: 'Chiudi',
  room_power: 'Alimentazione stanza', auto_off: 'Auto-spegnimento: DISATTIVO',
};

const ja: Strings = {
  unassigned: '未割当', assigned: '割当済み', no_room_joined: '部屋未参加',
  repair_title: '部屋を修理 (+1%)', repair_disabled: '修理はミッション開始後に利用可能です。',
  mission_running: 'ミッション中', lobby: 'ロビー',
  room_help: '部屋ヘルプ', room_help_subtitle: 'この部屋が制御するもの',
  room_help_body: '部屋を選択して説明を確認してください。',
  room_help_default: 'この部屋は現在のロールの操作を提供します。',
  room_help_select: '部屋を選択して操作を確認してください。',
  no_room: '部屋なし', select_room: '部屋を選択。',
  settings_title: '設定', close: '閉じる',
  settings_graphics: 'グラフィック', settings_antialiasing: 'アンチエイリアス',
  settings_vibration: '振動', settings_audio: 'デバイス音声', settings_language: '言語',
  quality_low: '低', quality_medium: '中', quality_high: '高', quality_ultra: 'ウルトラ',
  on: 'オン', off: 'オフ', vibration_high: '高', vibration_low: '低',
  rooms_title: '部屋',
  role_navigation: 'ナビゲーション', role_captain: '艦長', role_docking: 'ドッキング',
  role_pilot: 'パイロット', role_ballast: 'バラスト', role_sonar: 'ソナー',
  role_mining: '銛', role_battery: 'バッテリー', role_storage: '貯蔵・処理',
  role_life_support: '生命維持', role_drone: 'ドローン', role_engineer: 'エンジニアリング',
  role_engine: 'エンジン', role_periscope: '潜望鏡',
  help_navigation: 'マップを使って地形を確認し、ルートを計画しましょう。\n\nソナーを定期的にピングしてマップを解放しましょう！\n\nマップには危険ポイントや興味深い場所も表示されます。',
  help_captain: 'ミッションの目標、通信、チームの調整を統括します。',
  help_docking: 'ドッキング手順を管理します。\n\n潜水艦の下を見られる唯一のポジション—正確なドッキングに不可欠です。',
  help_pilot: '航行中の微細な操縦と針路を制御します。\n\nバラストとソナーと密に連携してください。\n\n速度が高いほど電力を消費します。',
  help_ballast: 'バラストタンクと浮力を管理します。\n\nバラスト調整は遅いプロセスです—事前に計画してください。',
  help_sonar: 'ソナーパルスを送って地形を把握しましょう。\n\nソナーは資源位置も返します。\n\n強いパルスはより多くの電力を消費します！',
  help_mining: '銛を操作して資源を集めます。発射、スキャン、回収を行います。',
  help_battery: '電力を監視・回復します。\n\n過充電ボタンで一時的に電力を増強できますが、船体にダメージがあります！',
  help_storage: '在庫を管理し、素材を処理します。',
  help_life_support: '酸素生成と客室温度を制御します。\n\nクルーの需要と電力のバランスを取ってください。',
  help_drone: 'ドローン操作室。近日公開。',
  help_engineer: '潜水艦全体のシステム診断と修理を管理します。',
  help_engine: 'エンジン出力を制御して各サブシステムに電力を供給します。\n\n燃料を効率的に使用するために常に調整が必要です！',
  help_periscope: '潜望鏡で周囲を調査します。\n\n強力なスポットライトを装備。焦点を調整してください。\n\nスポットライトは多くのエネルギーを使います—不要なときはオフにしてください。',
  nav_heading: 'ナビゲーション', nav_description: 'ノードマップ。ドラッグ、ピンチまたはスクロールでズーム。',
  nav_set_waypoint: 'ウェイポイント設定', nav_clear_waypoint: 'ウェイポイント削除',
  coming_soon: '近日公開', all_stop: '全停止', dive: '▼ 潜行', rise: '▲ 浮上',
  fire_emp: 'EMP発射', save_game: 'ゲーム保存',
  mining_laser_fire: '発射', mining_laser_stop: '回収', mining_laser_cooling: '再装填中...',
  mining_laser_armed: '銛準備完了。回収して再装填。', mining_laser_ready: '銛装填済み。',
  scanning: 'スキャン中...',
  reset_engine: 'エンジンリセット', change_fuel: '燃料交換', restore_power: '電力回復',
  unassign: '割当解除', kick: 'キック', confirm: '確認？',
  no_missions: 'ミッションが読み込まれていません。', no_crew: 'クルーが接続されていません。',
  quick_filter: 'クイックフィルタ', search: '⌕ 検索', crafting: 'クラフト',
  pump_status: 'ポンプ状態', current_power_drains: '現在の電力消費',
  fuel_loaded: '燃料搭載完了。', no_fuel_stopped: '燃料なし — エンジン停止',
  engine_reset: 'エンジンリセット', lox_boost_off: 'LOXブースト: オフ',
  throttle: 'スロットル', actual: '実際', target: '目標',
  battery_warning: '警告: 設定されたpingに対してバッテリーレベルが低すぎます！',
  select_fuel: '貯蔵・処理から補給済みのカニスターを選択してください。',
  change_fuel_heading: '燃料交換', changing_fuel: '燃料交換中', close_btn: '閉じる',
  room_power: '部屋の電力', auto_off: '自動オフ: オフ',
};

const ko: Strings = {
  unassigned: '미배정', assigned: '배정됨', no_room_joined: '방 없음',
  repair_title: '방 수리 (+1%)', repair_disabled: '수리는 미션 시작 후 이용 가능합니다.',
  mission_running: '미션 진행 중', lobby: '로비',
  room_help: '방 도움말', room_help_subtitle: '이 방이 제어하는 것',
  room_help_body: '방을 선택하여 설명을 확인하세요.',
  room_help_default: '이 방은 현재 역할의 컨트롤을 제공합니다.',
  room_help_select: '방을 선택하여 컨트롤을 확인하세요.',
  no_room: '방 없음', select_room: '방을 선택하세요.',
  settings_title: '설정', close: '닫기',
  settings_graphics: '그래픽', settings_antialiasing: '안티앨리어싱',
  settings_vibration: '진동', settings_audio: '기기 오디오', settings_language: '언어',
  quality_low: '낮음', quality_medium: '중간', quality_high: '높음', quality_ultra: '울트라',
  on: '켜기', off: '끄기', vibration_high: '높음', vibration_low: '낮음',
  rooms_title: '방',
  role_navigation: '항법', role_captain: '선장', role_docking: '도킹',
  role_pilot: '조종사', role_ballast: '밸러스트', role_sonar: '소나',
  role_mining: '작살', role_battery: '배터리', role_storage: '저장 및 처리',
  role_life_support: '생명 유지', role_drone: '드론', role_engineer: '공학',
  role_engine: '엔진', role_periscope: '잠망경',
  help_navigation: '지도를 사용하여 공개된 지형을 확인하고 경로를 계획하세요.\n\n소나를 정기적으로 핑하여 더 많은 지도를 공개하세요!\n\n지도에는 위험 요소와 관심 지점도 표시됩니다.',
  help_captain: '미션 목표, 통신, 팀 조율을 감독합니다.',
  help_docking: '도킹 절차를 관리합니다.\n\n잠수함 아래를 볼 수 있는 유일한 위치 — 정확한 도킹에 필수적입니다.',
  help_pilot: '항행 중 세밀한 조종과 방향을 제어합니다.\n\n밸러스트 및 소나와 긴밀히 협력하세요.\n\n높은 속도는 더 많은 전력을 소비합니다.',
  help_ballast: '밸러스트 탱크를 관리합니다.\n\n밸러스트 조정은 느린 과정입니다 — 미리 계획하세요.',
  help_sonar: '소나 펄스를 보내 환경을 파악하세요.\n\n소나는 자원 위치도 반환합니다.\n\n강한 펄스는 더 많은 전력을 소비합니다!',
  help_mining: '작살을 조작하여 자원을 수집합니다. 발사, 스캔, 회수를 수행하세요.',
  help_battery: '전력 비축량을 모니터링하고 복원합니다.\n\n과충전 버튼으로 일시적으로 배터리를 충전할 수 있습니다 — 선체 손상 위험!',
  help_storage: '인벤토리를 관리하고 미션을 위한 재료를 처리합니다.',
  help_life_support: '산소 생성과 선실 온도를 제어합니다.\n\n승무원의 필요와 에너지 균형을 맞추세요.',
  help_drone: '드론 운용실. 곧 출시 예정.',
  help_engineer: '잠수함 전체의 시스템 진단 및 수리를 관리합니다.',
  help_engine: '잠수함의 다양한 하위 시스템에 충분한 전력을 공급하도록 엔진 출력을 제어합니다.\n\n연료를 효율적으로 사용하기 위해 지속적인 조정이 필요합니다!',
  help_periscope: '잠망경으로 잠수함 주변을 조사하세요.\n\n강력한 스포트라이트 장착 — 초점을 조정하세요.\n\n스포트라이트는 에너지를 많이 사용합니다; 필요하지 않을 때는 끄세요.',
  nav_heading: '항법', nav_description: '노드 지도. 드래그, 핀치 또는 스크롤로 이동.',
  nav_set_waypoint: '웨이포인트 설정', nav_clear_waypoint: '웨이포인트 삭제',
  coming_soon: '출시 예정', all_stop: '전속 정지', dive: '▼ 잠수', rise: '▲ 부상',
  fire_emp: 'EMP 발사', save_game: '게임 저장',
  mining_laser_fire: '발사', mining_laser_stop: '회수', mining_laser_cooling: '재장전 중...',
  mining_laser_armed: '작살 장전됨. 회수하여 재장전.', mining_laser_ready: '작살 준비 완료.',
  scanning: '스캔 중...',
  reset_engine: '엔진 리셋', change_fuel: '연료 교체', restore_power: '전력 복원',
  unassign: '배정 해제', kick: '추방', confirm: '확인?',
  no_missions: '로드된 미션 없음.', no_crew: '연결된 승무원 없음.',
  quick_filter: '빠른 필터', search: '⌕ 검색', crafting: '제작',
  pump_status: '펌프 상태', current_power_drains: '현재 전력 소비',
  fuel_loaded: '연료 탑재 완료.', no_fuel_stopped: '연료 없음 — 엔진 정지',
  engine_reset: '엔진 리셋', lox_boost_off: 'LOX 부스트: 꺼짐',
  throttle: '스로틀', actual: '실제', target: '목표',
  battery_warning: '경고: 설정된 핑에 대해 배터리 레벨이 너무 낮습니다!',
  select_fuel: '저장 및 처리에서 보충된 캐니스터를 선택하세요.',
  change_fuel_heading: '연료 교체', changing_fuel: '연료 교체 중', close_btn: '닫기',
  room_power: '방 전력', auto_off: '자동 끄기: 꺼짐',
};

const zh: Strings = {
  unassigned: '未分配', assigned: '已分配', no_room_joined: '未加入房间',
  repair_title: '修复房间 (+1%)', repair_disabled: '任务开始后可进行修复。',
  mission_running: '任务进行中', lobby: '大厅',
  room_help: '房间帮助', room_help_subtitle: '此房间控制的内容',
  room_help_body: '选择房间查看其描述。',
  room_help_default: '此房间为当前角色提供控制。',
  room_help_select: '选择房间查看其控制。',
  no_room: '无房间', select_room: '选择房间。',
  settings_title: '设置', close: '关闭',
  settings_graphics: '画质', settings_antialiasing: '抗锯齿',
  settings_vibration: '振动', settings_audio: '设备音频', settings_language: '语言',
  quality_low: '低', quality_medium: '中', quality_high: '高', quality_ultra: '超高',
  on: '开', off: '关', vibration_high: '高', vibration_low: '低',
  rooms_title: '房间',
  role_navigation: '导航', role_captain: '船长', role_docking: '对接',
  role_pilot: '驾驶员', role_ballast: '压载', role_sonar: '声纳',
  role_mining: '鱼叉', role_battery: '电池', role_storage: '储存与处理',
  role_life_support: '生命维持', role_drone: '无人机', role_engineer: '工程',
  role_engine: '引擎', role_periscope: '潜望镜',
  help_navigation: '使用地图查看已揭示的地形并规划路线。\n\n定期对声纳进行ping以揭示更多地图！\n\n地图还显示危险区域和兴趣点。',
  help_captain: '监督任务目标、通信和团队协调。',
  help_docking: '处理对接程序。\n\n这是唯一能看到潜艇下方的位置——对于精确对接至关重要。',
  help_pilot: '控制精细机动和航向。\n\n与压载和声纳密切配合。\n\n较高的推力速度消耗更多电力。',
  help_ballast: '管理压载舱和浮力。\n\n调整压载是一个缓慢的过程——提前计划。',
  help_sonar: '发送声纳脉冲以揭示环境。\n\n声纳还返回资源位置。\n\n更强的脉冲消耗更多电力！',
  help_mining: '操作鱼叉以收集资源。发射、扫描并回收。',
  help_battery: '监控并恢复电力储备。\n\n使用过载按钮临时提升电池电量——有损坏船体的风险！',
  help_storage: '整理库存并处理物资以支持任务。',
  help_life_support: '控制氧气生成和舱内温度。\n\n平衡船员需求与可用能源。',
  help_drone: '无人机操作舱。敬请期待。',
  help_engineer: '管理潜艇的系统诊断和维修。',
  help_engine: '控制引擎输出以为各子系统提供足够的电力。\n\n需要持续调整以高效使用宝贵的燃料！',
  help_periscope: '使用潜望镜勘察潜艇周围环境。\n\n配备强力探照灯，调整焦距。\n\n探照灯耗能很大；不需要时请关闭。',
  nav_heading: '导航', nav_description: '节点地图。拖动平移，捏合或滚动缩放。',
  nav_set_waypoint: '设置航点', nav_clear_waypoint: '清除航点',
  coming_soon: '即将推出', all_stop: '全速停止', dive: '▼ 下潜', rise: '▲ 上浮',
  fire_emp: '发射电磁脉冲', save_game: '保存游戏',
  mining_laser_fire: '发射', mining_laser_stop: '回收', mining_laser_cooling: '装填中...',
  mining_laser_armed: '鱼叉已装填。回收以重新装填。', mining_laser_ready: '鱼叉准备就绪。',
  scanning: '扫描中...',
  reset_engine: '重置引擎', change_fuel: '更换燃料', restore_power: '恢复电力',
  unassign: '取消分配', kick: '踢出', confirm: '确认？',
  no_missions: '没有加载任务。', no_crew: '没有船员连接。',
  quick_filter: '快速筛选', search: '⌕ 搜索', crafting: '合成',
  pump_status: '泵状态', current_power_drains: '当前功耗',
  fuel_loaded: '燃料已装载。', no_fuel_stopped: '无燃料 — 引擎停止',
  engine_reset: '重置引擎', lox_boost_off: 'LOX加速：关闭',
  throttle: '油门', actual: '实际', target: '目标',
  battery_warning: '警告：电池电量过低，无法完成配置的ping！',
  select_fuel: '从储存与处理中选择一个已存储的罐。',
  change_fuel_heading: '更换燃料', changing_fuel: '正在更换燃料', close_btn: '关闭',
  room_power: '房间电力', auto_off: '自动关闭：关闭',
};

const nl: Strings = {
  unassigned: 'Niet toegewezen', assigned: 'Toegewezen', no_room_joined: 'Geen kamer',
  repair_title: 'Kamer repareren (+1%)', repair_disabled: 'Reparaties beschikbaar als missie start.',
  mission_running: 'Missie actief', lobby: 'Lobby',
  room_help: 'Kamerhulp', room_help_subtitle: 'Wat deze kamer bestuurt',
  room_help_body: 'Selecteer een kamer om de beschrijving te zien.',
  room_help_default: 'Deze kamer biedt bediening voor de huidige rol.',
  room_help_select: 'Selecteer een kamer om de bediening te zien.',
  no_room: 'Geen kamer', select_room: 'Selecteer een kamer.',
  settings_title: 'Instellingen', close: 'Sluiten',
  settings_graphics: 'Grafisch', settings_antialiasing: 'Anti-aliasing',
  settings_vibration: 'Trillen', settings_audio: 'Apparaataudio', settings_language: 'Taal',
  quality_low: 'Laag', quality_medium: 'Middel', quality_high: 'Hoog', quality_ultra: 'Ultra',
  on: 'Aan', off: 'Uit', vibration_high: 'Hoog', vibration_low: 'Laag',
  rooms_title: 'Kamers',
  role_navigation: 'Navigatie', role_captain: 'Kapitein', role_docking: 'Dok',
  role_pilot: 'Piloot', role_ballast: 'Ballast', role_sonar: 'Sonar',
  role_mining: 'Harpoen', role_battery: 'Batterij', role_storage: 'Opslag & Verwerking',
  role_life_support: 'Levensondersteuning', role_drone: 'Drones', role_engineer: 'Techniek',
  role_engine: 'Motor', role_periscope: 'Periscoop',
  help_navigation: 'Gebruik de kaart om terrein te zien en je route te plannen.\n\nVergeet niet de sonar te pingen!\n\nDe kaart toont ook gevaren en interessante punten.',
  help_captain: 'Bewaakt missiondoelen, communicatie en teamcoördinatie.',
  help_docking: 'Beheert dokprocedures.\n\nDe enige positie met zicht onder de onderzeeër — cruciaal voor nauwkeurig dok.',
  help_pilot: 'Beheert fijne manoeuvres en koers.\n\nWerk samen met ballast en sonar.\n\nHogere snelheden verbruiken meer energie.',
  help_ballast: 'Beheert ballasttanks.\n\nBallast aanpassen is traag — plan vooruit.',
  help_sonar: 'Stuur sonarimpulsen.\n\nSonar geeft ook resourcelocaties.\n\nSterkere impulsen verbruiken veel meer energie!',
  help_mining: 'Bedient de harpoen voor het verzamelen van resources. Vuur, scan en herstel.',
  help_battery: 'Bewaakt en herstelt energiereserves.\n\nGebruik overbelasting voor tijdelijke boost — risico op rompschade!',
  help_storage: 'Organiseert inventaris en verwerkt materialen.',
  help_life_support: 'Beheert zuurstof en temperatuur.\n\nBalanceer behoeften van de bemanning met energie.',
  help_drone: 'Drone operatieruimte. Binnenkort beschikbaar.',
  help_engineer: 'Beheert systeemdiagnostiek en reparaties.',
  help_engine: 'Beheert motorvermogen.\n\nConstante aanpassingen voor efficiënt brandstofgebruik!',
  help_periscope: 'Gebruik de periscoop.\n\nKrachtige schijnwerper.\n\nVeel energie; schakel uit wanneer niet nodig.',
  nav_heading: 'Navigatie', nav_description: 'Knooppuntkaart. Sleep, knijp of scroll.',
  nav_set_waypoint: 'Waypoint instellen', nav_clear_waypoint: 'Waypoint wissen',
  coming_soon: 'Binnenkort', all_stop: 'VOLLEDIG STOP', dive: '▼ DUIKEN', rise: '▲ STIJGEN',
  fire_emp: 'EMP AFVUREN', save_game: 'Opslaan',
  mining_laser_fire: 'AFVUREN', mining_laser_stop: 'OPHALEN', mining_laser_cooling: 'LADEN...',
  mining_laser_armed: 'Harpoen gewapend. Ophalen om te herladen.', mining_laser_ready: 'Harpoen klaar.',
  scanning: 'Scannen...',
  reset_engine: 'Motor resetten', change_fuel: 'Brandstof wisselen', restore_power: 'Energie herstellen',
  unassign: 'Onttoewijzen', kick: 'Verwijderen', confirm: 'Bevestigen?',
  no_missions: 'Geen missies geladen.', no_crew: 'Geen bemanning verbonden.',
  quick_filter: 'Snelfilter', search: '⌕ Zoeken', crafting: 'Vervaardigen',
  pump_status: 'POMPSTATUS', current_power_drains: 'Huidige verbruikers',
  fuel_loaded: 'Brandstof geladen.', no_fuel_stopped: 'GEEN BRANDSTOF — MOTOR GESTOPT',
  engine_reset: 'Motor resetten', lox_boost_off: 'LOX boost: UIT',
  throttle: 'gashendel', actual: 'HUIDIG', target: 'DOEL',
  battery_warning: 'Waarschuwing: batterijniveau te laag voor geconfigureerde ping!',
  select_fuel: 'Selecteer een voorraadkanister uit Opslag & Verwerking.',
  change_fuel_heading: 'Brandstof wisselen', changing_fuel: 'Brandstof wisselen', close_btn: 'Sluiten',
  room_power: 'Ruimtestroom', auto_off: 'Auto-uit: UIT',
};

const ru: Strings = {
  unassigned: 'Не назначен', assigned: 'Назначен', no_room_joined: 'Нет комнаты',
  repair_title: 'Починить комнату (+1%)', repair_disabled: 'Ремонт доступен после начала миссии.',
  mission_running: 'Миссия идёт', lobby: 'Лобби',
  room_help: 'Справка', room_help_subtitle: 'Что управляет эта комната',
  room_help_body: 'Выберите комнату для просмотра описания.',
  room_help_default: 'Эта комната предоставляет управление для текущей роли.',
  room_help_select: 'Выберите комнату для просмотра управления.',
  no_room: 'Нет комнаты', select_room: 'Выберите комнату.',
  settings_title: 'Настройки', close: 'Закрыть',
  settings_graphics: 'Графика', settings_antialiasing: 'Сглаживание',
  settings_vibration: 'Вибрация', settings_audio: 'Аудио', settings_language: 'Язык',
  quality_low: 'Низкое', quality_medium: 'Среднее', quality_high: 'Высокое', quality_ultra: 'Ультра',
  on: 'Вкл', off: 'Выкл', vibration_high: 'Высокая', vibration_low: 'Низкая',
  rooms_title: 'Комнаты',
  role_navigation: 'Навигация', role_captain: 'Капитан', role_docking: 'Стыковка',
  role_pilot: 'Пилот', role_ballast: 'Балласт', role_sonar: 'Сонар',
  role_mining: 'Гарпун', role_battery: 'Батарея', role_storage: 'Хранение и обработка',
  role_life_support: 'Жизнеобеспечение', role_drone: 'Дроны', role_engineer: 'Инженерия',
  role_engine: 'Двигатель', role_periscope: 'Перископ',
  help_navigation: 'Используйте карту для просмотра рельефа и планирования маршрута.\n\nРегулярно пингуйте сонар!\n\nКарта также показывает опасности и точки интереса.',
  help_captain: 'Контролирует цели миссии, связь и координацию команды.',
  help_docking: 'Управляет процедурами стыковки.\n\nЕдинственная позиция с обзором ниже подводной лодки.',
  help_pilot: 'Управляет манёврами и курсом.\n\nТесно сотрудничайте с балластом и сонаром.\n\nВысокие скорости потребляют больше энергии.',
  help_ballast: 'Управляет балластными цистернами.\n\nРегулировка балласта медленная — планируйте заранее.',
  help_sonar: 'Посылайте сонарные импульсы.\n\nСонар определяет расположение ресурсов.\n\nБолее мощные импульсы потребляют значительно больше энергии!',
  help_mining: 'Управляет гарпуном для захвата ресурсов. Стреляйте, сканируйте и возвращайте.',
  help_battery: 'Контролирует и восстанавливает энергозапасы.\n\nИспользуйте перезарядку с риском повреждения корпуса!',
  help_storage: 'Управляет инвентарём и перерабатывает материалы.',
  help_life_support: 'Управляет кислородом и температурой.\n\nБалансируйте потребности экипажа с доступной энергией.',
  help_drone: 'Отсек дронов. Скоро.',
  help_engineer: 'Управляет диагностикой и ремонтом.',
  help_engine: 'Управляет мощностью двигателя.\n\nПостоянные корректировки для эффективного расхода топлива!',
  help_periscope: 'Используйте перископ для осмотра окружения.\n\nМощный прожектор.\n\nВыключайте, когда не нужен.',
  nav_heading: 'Навигация', nav_description: 'Карта узлов. Перетаскивайте, сжимайте или прокручивайте.',
  nav_set_waypoint: 'Задать точку', nav_clear_waypoint: 'Удалить точку',
  coming_soon: 'Скоро', all_stop: 'ПОЛНАЯ ОСТАНОВКА', dive: '▼ ПОГРУЖЕНИЕ', rise: '▲ ПОДЪЁМ',
  fire_emp: 'ПРИМЕНИТЬ ЭМИ', save_game: 'Сохранить игру',
  mining_laser_fire: 'ОГОНЬ', mining_laser_stop: 'ВЕРНУТЬ', mining_laser_cooling: 'ПЕРЕЗАРЯДКА...',
  mining_laser_armed: 'Гарпун готов. Вернуть для перезарядки.', mining_laser_ready: 'Гарпун заряжен.',
  scanning: 'Сканирование...',
  reset_engine: 'Сбросить двигатель', change_fuel: 'Сменить топливо', restore_power: 'Восстановить питание',
  unassign: 'Убрать назначение', kick: 'Выгнать', confirm: 'Подтвердить?',
  no_missions: 'Миссии не загружены.', no_crew: 'Экипаж не подключён.',
  quick_filter: 'Быстрый фильтр', search: '⌕ Поиск', crafting: 'Крафт',
  pump_status: 'НАСОСЫ', current_power_drains: 'Текущие потребители',
  fuel_loaded: 'Топливо загружено.', no_fuel_stopped: 'НЕТ ТОПЛИВА — ДВИГАТЕЛЬ ОСТАНОВЛЕН',
  engine_reset: 'Сбросить двигатель', lox_boost_off: 'Буст ЖК: ВЫКЛ',
  throttle: 'тяга', actual: 'ФАКТ', target: 'ЦЕЛЬ',
  battery_warning: 'Предупреждение: уровень батареи слишком низкий для пинга!',
  select_fuel: 'Выберите канистру из Хранения и обработки.',
  change_fuel_heading: 'Смена топлива', changing_fuel: 'Смена топлива', close_btn: 'Закрыть',
  room_power: 'Питание комнаты', auto_off: 'Авто-выкл: ВЫКЛ',
};

const pl: Strings = {
  unassigned: 'Nieprzypisany', assigned: 'Przypisany', no_room_joined: 'Brak pokoju',
  repair_title: 'Napraw pokój (+1%)', repair_disabled: 'Naprawa dostępna po rozpoczęciu misji.',
  mission_running: 'Misja trwa', lobby: 'Lobby',
  room_help: 'Pomoc pokoju', room_help_subtitle: 'Co kontroluje ten pokój',
  room_help_body: 'Wybierz pokój, aby zobaczyć jego opis.',
  room_help_default: 'Ten pokój zapewnia sterowanie dla bieżącej roli.',
  room_help_select: 'Wybierz pokój, aby zobaczyć sterowanie.',
  no_room: 'Brak pokoju', select_room: 'Wybierz pokój.',
  settings_title: 'Ustawienia', close: 'Zamknij',
  settings_graphics: 'Grafika', settings_antialiasing: 'Antyaliasing',
  settings_vibration: 'Wibracja', settings_audio: 'Dźwięk urządzenia', settings_language: 'Język',
  quality_low: 'Niska', quality_medium: 'Średnia', quality_high: 'Wysoka', quality_ultra: 'Ultra',
  on: 'Włączone', off: 'Wyłączone', vibration_high: 'Wysoka', vibration_low: 'Niska',
  rooms_title: 'Pokoje',
  role_navigation: 'Nawigacja', role_captain: 'Kapitan', role_docking: 'Dokowanie',
  role_pilot: 'Pilot', role_ballast: 'Balast', role_sonar: 'Sonar',
  role_mining: 'Harpun', role_battery: 'Bateria', role_storage: 'Magazyn i obróbka',
  role_life_support: 'Podtrzymanie życia', role_drone: 'Drony', role_engineer: 'Inżynieria',
  role_engine: 'Silnik', role_periscope: 'Peryskop',
  help_navigation: 'Używaj mapy, aby widzieć odkryty teren i planować trasę.\n\nRegularnie pinguj sonar!\n\nMapa pokazuje też zagrożenia i punkty zainteresowania.',
  help_captain: 'Nadzoruje cele misji, komunikację i koordynację zespołu.',
  help_docking: 'Zarządza procedurami dokowania.\n\nJedyna pozycja z widokiem pod łodzią podwodną.',
  help_pilot: 'Kontroluje manewry i kurs.\n\nWspółpracuj z balastem i sonarem.\n\nWyższe prędkości zużywają więcej energii.',
  help_ballast: 'Zarządza zbiornikami balastowymi.\n\nRegulacja balastu jest wolna — planuj z wyprzedzeniem.',
  help_sonar: 'Wysyłaj impulsy sonarowe.\n\nSonar zwraca też lokalizacje zasobów.\n\nMocniejsze impulsy zużywają znacznie więcej energii!',
  help_mining: 'Obsługuje harpun do zbierania zasobów. Strzelaj, skanuj i odzyskuj.',
  help_battery: 'Monitoruje i przywraca rezerwy energii.\n\nUżyj przeładowania z ryzykiem uszkodzenia kadłuba!',
  help_storage: 'Organizuje ekwipunek i przetwarza materiały.',
  help_life_support: 'Kontroluje generowanie tlenu i temperaturę.\n\nRównoważ potrzeby załogi z dostępną energią.',
  help_drone: 'Sekcja dronów. Wkrótce.',
  help_engineer: 'Zarządza diagnostyką i naprawami.',
  help_engine: 'Kontroluje moc silnika.\n\nStałe regulacje dla efektywnego użycia paliwa!',
  help_periscope: 'Używaj peryskop do obserwacji.\n\nMocny reflektor.\n\nReflektor zużywa dużo energii; wyłączaj gdy niepotrzebny.',
  nav_heading: 'Nawigacja', nav_description: 'Mapa węzłów. Przeciągnij, szczypnij lub przewiń.',
  nav_set_waypoint: 'Ustaw punkt', nav_clear_waypoint: 'Usuń punkt',
  coming_soon: 'Wkrótce', all_stop: 'PEŁNY STOP', dive: '▼ NURKUJ', rise: '▲ WYNURZ',
  fire_emp: 'ODPAL EMP', save_game: 'Zapisz grę',
  mining_laser_fire: 'STRZELAJ', mining_laser_stop: 'ODZYSKAJ', mining_laser_cooling: 'PRZEŁADOWANIE...',
  mining_laser_armed: 'Harpun naładowany. Odzyskaj do przeładowania.', mining_laser_ready: 'Harpun gotowy.',
  scanning: 'Skanowanie...',
  reset_engine: 'Zresetuj silnik', change_fuel: 'Zmień paliwo', restore_power: 'Przywróć zasilanie',
  unassign: 'Odprzypisz', kick: 'Wyrzuć', confirm: 'Potwierdź?',
  no_missions: 'Brak załadowanych misji.', no_crew: 'Brak połączonej załogi.',
  quick_filter: 'Szybki filtr', search: '⌕ Szukaj', crafting: 'Rzemiosło',
  pump_status: 'STATUS POMP', current_power_drains: 'Aktualne obciążenia',
  fuel_loaded: 'Paliwo załadowane.', no_fuel_stopped: 'BRAK PALIWA — SILNIK ZATRZYMANY',
  engine_reset: 'Zresetuj silnik', lox_boost_off: 'Boost LOX: WYŁĄCZONY',
  throttle: 'przepustnica', actual: 'RZECZYWISTY', target: 'DOCELOWY',
  battery_warning: 'Ostrzeżenie: poziom baterii zbyt niski dla skonfigurowanego pingu!',
  select_fuel: 'Wybierz kanister z Magazynu i obróbki.',
  change_fuel_heading: 'Zmiana paliwa', changing_fuel: 'Zmiana paliwa', close_btn: 'Zamknij',
  room_power: 'Zasilanie pokoju', auto_off: 'Auto-wyłączenie: WYŁĄCZONE',
};

const sv: Strings = {
  unassigned: 'Ej tilldelad', assigned: 'Tilldelad', no_room_joined: 'Inget rum',
  repair_title: 'Reparera rum (+1%)', repair_disabled: 'Reparationer tillgängliga när uppdraget startar.',
  mission_running: 'Uppdrag pågår', lobby: 'Lobby',
  room_help: 'Rumshjälp', room_help_subtitle: 'Vad detta rum styr',
  room_help_body: 'Välj ett rum för att se dess beskrivning.',
  room_help_default: 'Det här rummet ger kontroller för den aktuella rollen.',
  room_help_select: 'Välj ett rum för att se dess kontroller.',
  no_room: 'Inget rum', select_room: 'Välj ett rum.',
  settings_title: 'Inställningar', close: 'Stäng',
  settings_graphics: 'Grafik', settings_antialiasing: 'Kantutjämning',
  settings_vibration: 'Vibration', settings_audio: 'Enhetljud', settings_language: 'Språk',
  quality_low: 'Låg', quality_medium: 'Medel', quality_high: 'Hög', quality_ultra: 'Ultra',
  on: 'På', off: 'Av', vibration_high: 'Hög', vibration_low: 'Låg',
  rooms_title: 'Rum',
  role_navigation: 'Navigation', role_captain: 'Kapten', role_docking: 'Docking',
  role_pilot: 'Pilot', role_ballast: 'Ballast', role_sonar: 'Sonar',
  role_mining: 'Harpun', role_battery: 'Batteri', role_storage: 'Lager & Bearbetning',
  role_life_support: 'Livsstöd', role_drone: 'Drönare', role_engineer: 'Teknik',
  role_engine: 'Motor', role_periscope: 'Periskop',
  help_navigation: 'Använd kartan för att se avslöjat terräng och planera din rutt.\n\nKom ihåg att regelbundet pinga sonaren!\n\nKartan visar också faror och intressanta platser.',
  help_captain: 'Övervakar uppdragsmål, kommunikation och teamkoordinering.',
  help_docking: 'Hanterar dockningsprocedurer.\n\nEnda positionen med vy under ubåten.',
  help_pilot: 'Styr manövrering och kurs.\n\nSamarbeta nära med ballast och sonar.\n\nHögre hastigheter förbrukar mer energi.',
  help_ballast: 'Hanterar ballasttankar.\n\nBallastjustering är långsam — planera i förväg.',
  help_sonar: 'Skicka sonarpulser.\n\nSonaren returnerar resursplatser.\n\nStarkare pulser förbrukar mycket mer energi!',
  help_mining: 'Hanterar harpunen för att samla resurser. Skjut, skanna och hämta.',
  help_battery: 'Övervakar och återställer energireserver.\n\nAnvänd överladdning med risk för skrovet!',
  help_storage: 'Organiserar inventarier och bearbetar material.',
  help_life_support: 'Styr syrgenerering och kabinentemperatur.\n\nBalansera besättningens behov.',
  help_drone: 'Drönardriftsbay. Kommer snart.',
  help_engineer: 'Hanterar systemdiagnostik och reparationer.',
  help_engine: 'Styr motoreffekt.\n\nKonstanta justeringar krävs för effektiv bränsleanvändning!',
  help_periscope: 'Använd periskopet för att undersöka omgivningen.\n\nKraftig strålkastare.\n\nStäng av när den inte behövs.',
  nav_heading: 'Navigation', nav_description: 'Nodkarta. Dra, nyp eller scrolla.',
  nav_set_waypoint: 'Sätt waypoint', nav_clear_waypoint: 'Ta bort waypoint',
  coming_soon: 'Kommer snart', all_stop: 'FULLT STOPP', dive: '▼ DYK', rise: '▲ STIGA',
  fire_emp: 'AVFYRA EMP', save_game: 'Spara spel',
  mining_laser_fire: 'AVFYRA', mining_laser_stop: 'HÄMTA', mining_laser_cooling: 'LADDAR...',
  mining_laser_armed: 'Harpun beväpnad. Hämta för omladdning.', mining_laser_ready: 'Harpun redo.',
  scanning: 'Skannar...',
  reset_engine: 'Återställ motor', change_fuel: 'Byt bränsle', restore_power: 'Återställ ström',
  unassign: 'Avtilldela', kick: 'Sparka', confirm: 'Bekräfta?',
  no_missions: 'Inga uppdrag laddade.', no_crew: 'Ingen besättning ansluten.',
  quick_filter: 'Snabbfilter', search: '⌕ Sök', crafting: 'Hantverk',
  pump_status: 'PUMPSTATUS', current_power_drains: 'Aktuella förbrukare',
  fuel_loaded: 'Bränsle laddat.', no_fuel_stopped: 'INGET BRÄNSLE — MOTOR STOPPAD',
  engine_reset: 'Återställ motor', lox_boost_off: 'LOX-boost: AV',
  throttle: 'spjäll', actual: 'FAKTISK', target: 'MÅL',
  battery_warning: 'Varning: batterinivå för låg för konfigurerat ping!',
  select_fuel: 'Välj en lagrad kanister från Lager & Bearbetning.',
  change_fuel_heading: 'Byt Bränsle', changing_fuel: 'Byter Bränsle', close_btn: 'Stäng',
  room_power: 'Rumsström', auto_off: 'Auto-av: AV',
};

const tr: Strings = {
  unassigned: 'Atanmamış', assigned: 'Atandı', no_room_joined: 'Oda yok',
  repair_title: 'Odayı onar (+1%)', repair_disabled: 'Onarımlar görev başladıktan sonra kullanılabilir.',
  mission_running: 'Görev devam ediyor', lobby: 'Lobi',
  room_help: 'Oda yardımı', room_help_subtitle: 'Bu odanın kontrolü',
  room_help_body: 'Açıklama için bir oda seçin.',
  room_help_default: 'Bu oda, mevcut rol için kontroller sağlar.',
  room_help_select: 'Kontrolleri görmek için bir oda seçin.',
  no_room: 'Oda yok', select_room: 'Oda seçin.',
  settings_title: 'Ayarlar', close: 'Kapat',
  settings_graphics: 'Grafik', settings_antialiasing: 'Kenar Yumuşatma',
  settings_vibration: 'Titreşim', settings_audio: 'Cihaz sesi', settings_language: 'Dil',
  quality_low: 'Düşük', quality_medium: 'Orta', quality_high: 'Yüksek', quality_ultra: 'Ultra',
  on: 'Açık', off: 'Kapalı', vibration_high: 'Yüksek', vibration_low: 'Düşük',
  rooms_title: 'Odalar',
  role_navigation: 'Navigasyon', role_captain: 'Kaptan', role_docking: 'Yanaşma',
  role_pilot: 'Pilot', role_ballast: 'Balast', role_sonar: 'Sonar',
  role_mining: 'Zıpkın', role_battery: 'Batarya', role_storage: 'Depolama ve İşleme',
  role_life_support: 'Hayat Desteği', role_drone: 'İnsansız Hava Araçları', role_engineer: 'Mühendislik',
  role_engine: 'Motor', role_periscope: 'Periskop',
  help_navigation: 'Keşfedilen araziyi görmek ve rotanızı planlamak için haritayı kullanın.\n\nSonara düzenli ping atmayı unutmayın!\n\nHarita ayrıca tehlikeleri ve ilgi noktalarını gösterir.',
  help_captain: 'Görev hedeflerini, iletişimi ve takım koordinasyonunu denetler.',
  help_docking: 'Yanaşma prosedürlerini yönetir.\n\nDenizaltının altını görebilen tek pozisyon.',
  help_pilot: 'İnce manevra ve rotayı kontrol eder.\n\nBalast ve sonar ile yakın çalışın.\n\nYüksek hızlar daha fazla güç tüketir.',
  help_ballast: 'Balast tanklarını yönetir.\n\nBalast ayarlamak yavaştır — önceden planlayın.',
  help_sonar: 'Sonar darbeleri gönderin.\n\nSonar ayrıca kaynak konumlarını döndürür.\n\nDaha güçlü darbeler çok daha fazla güç tüketir!',
  help_mining: 'Kaynakları toplamak için zıpkını kullanır. Ateş edin, tarayın ve geri alın.',
  help_battery: 'Güç rezervlerini izler.\n\nGövde hasarı riskiyle geçici olarak şarjı artırmak için aşırı şarjı kullanın!',
  help_storage: 'Envanteri düzenler ve malzemeleri işler.',
  help_life_support: 'Oksijen üretimi ve kabin sıcaklığını kontrol eder.\n\nMürettebatın ihtiyaçlarını enerjiyle dengeleyin.',
  help_drone: 'İnsansız hava aracı birimi. Yakında.',
  help_engineer: 'Sistem diagnostiği ve onarımlarını yönetir.',
  help_engine: 'Motor çıkışını kontrol eder.\n\nVerimli yakıt kullanımı için sürekli ayarlamalar gereklidir!',
  help_periscope: 'Denizaltının çevresini incelemek için periskopu kullanın.\n\nGüçlü spot ışığı.\n\nGerekli olmadığında kapatın.',
  nav_heading: 'Navigasyon', nav_description: 'Düğüm haritası. Kaydırın, kıstırın veya zum için kaydırın.',
  nav_set_waypoint: 'Ara nokta ayarla', nav_clear_waypoint: 'Ara noktayı temizle',
  coming_soon: 'Yakında', all_stop: 'TAM DURUŞ', dive: '▼ DALIŞ', rise: '▲ YÜKSEL',
  fire_emp: 'EMP ATEŞLE', save_game: 'Oyunu kaydet',
  mining_laser_fire: 'ATEŞ', mining_laser_stop: 'GERİ AL', mining_laser_cooling: 'DOLDUR...',
  mining_laser_armed: 'Zıpkın hazır. Geri almak için yeniden doldur.', mining_laser_ready: 'Zıpkın hazır.',
  scanning: 'Taranıyor...',
  reset_engine: 'Motoru sıfırla', change_fuel: 'Yakıtı değiştir', restore_power: 'Gücü geri yükle',
  unassign: 'Atamasını kaldır', kick: 'Çıkar', confirm: 'Onayla?',
  no_missions: 'Görev yüklenmedi.', no_crew: 'Mürettebat bağlı değil.',
  quick_filter: 'Hızlı filtre', search: '⌕ Ara', crafting: 'Üretim',
  pump_status: 'POMPA DURUMU', current_power_drains: 'Mevcut güç tüketimi',
  fuel_loaded: 'Yakıt yüklendi.', no_fuel_stopped: 'YAKIT YOK — MOTOR DURDU',
  engine_reset: 'Motoru sıfırla', lox_boost_off: 'LOX boost: KAPALI',
  throttle: 'gaz', actual: 'GERÇEK', target: 'HEDEF',
  battery_warning: 'Uyarı: Yapılandırılan ping için pil seviyesi çok düşük!',
  select_fuel: 'Depolama ve İşlemeden dolu bir kanister seçin.',
  change_fuel_heading: 'Yakıt Değiştir', changing_fuel: 'Yakıt Değiştiriliyor', close_btn: 'Kapat',
  room_power: 'Oda gücü', auto_off: 'Otomatik kapatma: KAPALI',
};

const ar: Strings = {
  unassigned: 'غير مُعيَّن', assigned: 'مُعيَّن', no_room_joined: 'لم تنضم لغرفة',
  repair_title: 'إصلاح الغرفة (+1%)', repair_disabled: 'الإصلاحات متاحة بعد بدء المهمة.',
  mission_running: 'المهمة جارية', lobby: 'الردهة',
  room_help: 'مساعدة الغرفة', room_help_subtitle: 'ما تتحكم به هذه الغرفة',
  room_help_body: 'اختر غرفة لرؤية وصفها.',
  room_help_default: 'توفر هذه الغرفة عناصر تحكم للدور الحالي.',
  room_help_select: 'اختر غرفة لرؤية عناصر تحكمها.',
  no_room: 'لا غرفة', select_room: 'اختر غرفة.',
  settings_title: 'الإعدادات', close: 'إغلاق',
  settings_graphics: 'الرسومات', settings_antialiasing: 'تنعيم الحواف',
  settings_vibration: 'الاهتزاز', settings_audio: 'صوت الجهاز', settings_language: 'اللغة',
  quality_low: 'منخفض', quality_medium: 'متوسط', quality_high: 'مرتفع', quality_ultra: 'فائق',
  on: 'تشغيل', off: 'إيقاف', vibration_high: 'مرتفع', vibration_low: 'منخفض',
  rooms_title: 'الغرف',
  role_navigation: 'الملاحة', role_captain: 'القبطان', role_docking: 'الإرساء',
  role_pilot: 'الطيار', role_ballast: 'الصابورة', role_sonar: 'السونار',
  role_mining: 'الحربة', role_battery: 'البطارية', role_storage: 'التخزين والمعالجة',
  role_life_support: 'دعم الحياة', role_drone: 'الطائرات المسيّرة', role_engineer: 'الهندسة',
  role_engine: 'المحرك', role_periscope: 'المنظار',
  help_navigation: 'استخدم الخريطة لرؤية التضاريس المكشوفة وتحديد مسارك.\n\nأرسل نبضات السونار بانتظام لكشف المزيد!\n\nتُظهر الخريطة أيضًا المخاطر ونقاط الاهتمام.',
  help_captain: 'يشرف على أهداف المهمة والتواصل وتنسيق الفريق.',
  help_docking: 'يدير إجراءات الرسو.\n\nالموضع الوحيد برؤية أسفل الغواصة.',
  help_pilot: 'يتحكم في المناورات الدقيقة والاتجاه.\n\nتعاون مع الصابورة والسونار.\n\nالسرعات الأعلى تستهلك طاقة أكثر.',
  help_ballast: 'يدير خزانات الصابورة والطفو.\n\nضبط الصابورة بطيء — خطط مسبقًا.',
  help_sonar: 'أرسل نبضات السونار.\n\nيُعيد السونار مواقع الموارد.\n\nالنبضات الأقوى تستهلك طاقة أكبر!',
  help_mining: 'يشغّل الحربة لجمع الموارد. أطلق، امسح، واسترجع.',
  help_battery: 'يراقب احتياطيات الطاقة ويستعيدها.\n\nاستخدم الشحن الزائد مع خطر تلف الهيكل!',
  help_storage: 'ينظم المخزون ويعالج المواد.',
  help_life_support: 'يتحكم في توليد الأكسجين ودرجة الحرارة.\n\nوازن احتياجات الطاقم مع الطاقة المتاحة.',
  help_drone: 'خليج الطائرات المسيّرة. قريبًا.',
  help_engineer: 'يدير تشخيصات النظام والإصلاحات.',
  help_engine: 'يتحكم في إخراج المحرك.\n\nالتعديلات المستمرة ضرورية للاستخدام الفعّال للوقود!',
  help_periscope: 'استخدم المنظار لمسح محيط الغواصة.\n\nكشاف قوي.\n\nأطفئه عند عدم الحاجة.',
  nav_heading: 'الملاحة', nav_description: 'خريطة العقدة. اسحب للتحريك، اقرص أو مرر للتكبير.',
  nav_set_waypoint: 'تعيين نقطة مسار', nav_clear_waypoint: 'مسح نقطة المسار',
  coming_soon: 'قريبًا', all_stop: 'توقف كامل', dive: '▼ غطس', rise: '▲ صعود',
  fire_emp: 'أطلق EMP', save_game: 'حفظ اللعبة',
  mining_laser_fire: 'أطلق', mining_laser_stop: 'استرجع', mining_laser_cooling: 'إعادة تحميل...',
  mining_laser_armed: 'الحربة جاهزة. استرجع لإعادة التحميل.', mining_laser_ready: 'الحربة جاهزة.',
  scanning: 'مسح...',
  reset_engine: 'إعادة تشغيل المحرك', change_fuel: 'تغيير الوقود', restore_power: 'استعادة الطاقة',
  unassign: 'إلغاء التعيين', kick: 'طرد', confirm: 'تأكيد؟',
  no_missions: 'لا مهام محمّلة.', no_crew: 'لا طاقم متصل.',
  quick_filter: 'تصفية سريعة', search: '⌕ بحث', crafting: 'صنع',
  pump_status: 'حالة المضخة', current_power_drains: 'استهلاك الطاقة الحالي',
  fuel_loaded: 'الوقود محمّل.', no_fuel_stopped: 'لا وقود — المحرك متوقف',
  engine_reset: 'إعادة تشغيل المحرك', lox_boost_off: 'تعزيز LOX: إيقاف',
  throttle: 'عجلة الغاز', actual: 'الفعلي', target: 'الهدف',
  battery_warning: 'تحذير: مستوى البطارية منخفض جدًا لإرسال النبضة المضبوطة!',
  select_fuel: 'اختر خزانًا من التخزين والمعالجة.',
  change_fuel_heading: 'تغيير الوقود', changing_fuel: 'جاري تغيير الوقود', close_btn: 'إغلاق',
  room_power: 'طاقة الغرفة', auto_off: 'إيقاف تلقائي: إيقاف',
};

const no: Strings = {
  unassigned: 'Ikke tildelt', assigned: 'Tildelt', no_room_joined: 'Inget rom',
  repair_title: 'Reparer rom (+1%)', repair_disabled: 'Reparasjoner tilgjengelige når oppdraget starter.',
  mission_running: 'Oppdrag pågår', lobby: 'Lobby',
  room_help: 'Romhjelp', room_help_subtitle: 'Hva dette rommet styrer',
  room_help_body: 'Velg et rom for å se beskrivelsen.',
  room_help_default: 'Dette rommet gir kontroller for den nåværende rollen.',
  room_help_select: 'Velg et rom for å se kontrollene.',
  no_room: 'Ingen rom', select_room: 'Velg et rom.',
  settings_title: 'Innstillinger', close: 'Lukk',
  settings_graphics: 'Grafikk', settings_antialiasing: 'Antialiasing',
  settings_vibration: 'Vibrasjon', settings_audio: 'Enhetslyd', settings_language: 'Språk',
  quality_low: 'Lav', quality_medium: 'Middels', quality_high: 'Høy', quality_ultra: 'Ultra',
  on: 'På', off: 'Av', vibration_high: 'Høy', vibration_low: 'Lav',
  rooms_title: 'Rom',
  role_navigation: 'Navigasjon', role_captain: 'Kaptein', role_docking: 'Dokking',
  role_pilot: 'Pilot', role_ballast: 'Ballast', role_sonar: 'Sonar',
  role_mining: 'Harpun', role_battery: 'Batteri', role_storage: 'Lagring og behandling',
  role_life_support: 'Livsstøtte', role_drone: 'Droner', role_engineer: 'Ingeniørfag',
  role_engine: 'Motor', role_periscope: 'Periskop',
  help_navigation: 'Bruk kartet for å se avdekket terreng og planlegge ruten.\n\nHusk å pinge sonaren regelmessig!\n\nKartet viser også farer og interessepunkter.',
  help_captain: 'Overvåker oppdragsmål, kommunikasjon og teamkoordinering.',
  help_docking: 'Håndterer dokkingprosedyrer.\n\nEneste posisjon med utsikt under ubåten.',
  help_pilot: 'Styrer manøvreringer og kurs.\n\nSamarbeid tett med ballast og sonar.\n\nHøyere hastigheter bruker mer energi.',
  help_ballast: 'Styrer ballasttanker.\n\nJustering av ballast er langsom — planlegg på forhånd.',
  help_sonar: 'Send sonarpulser.\n\nSonaren returnerer ressurslokasjoner.\n\nSterkere pulser bruker mye mer energi!',
  help_mining: 'Opererer harpunen for å samle ressurser. Skyt, skann og hent.',
  help_battery: 'Overvåker og gjenoppretter energireserver.\n\nBruk overbelastning med risiko for skrog!',
  help_storage: 'Organiserer inventar og prosesserer materialer.',
  help_life_support: 'Kontrollerer oksygen og temperatur.\n\nBalanser besetningens behov mot energi.',
  help_drone: 'Drone-operasjonsbay. Kommer snart.',
  help_engineer: 'Administrerer diagnostikk og reparasjoner.',
  help_engine: 'Kontrollerer motoreffekt.\n\nKonstante justeringer for effektiv drivstoffbruk!',
  help_periscope: 'Bruk periskopet for å undersøke omgivelsene.\n\nKraftig søkelys.\n\nSlå av når ikke nødvendig.',
  nav_heading: 'Navigasjon', nav_description: 'Nodekart. Dra, klyp eller rull.',
  nav_set_waypoint: 'Sett veipunkt', nav_clear_waypoint: 'Fjern veipunkt',
  coming_soon: 'Kommer snart', all_stop: 'FULL STOPP', dive: '▼ DYK', rise: '▲ STIG',
  fire_emp: 'AVFYR EMP', save_game: 'Lagre spill',
  mining_laser_fire: 'AVFYR', mining_laser_stop: 'HENT', mining_laser_cooling: 'LADER...',
  mining_laser_armed: 'Harpun ladet. Hent for omlasting.', mining_laser_ready: 'Harpun klar.',
  scanning: 'Skanner...',
  reset_engine: 'Tilbakestill motor', change_fuel: 'Bytt drivstoff', restore_power: 'Gjenopprett strøm',
  unassign: 'Fjern tildeling', kick: 'Spark', confirm: 'Bekreft?',
  no_missions: 'Ingen oppdrag lastet.', no_crew: 'Ingen besetning tilkoblet.',
  quick_filter: 'Hurtigfilter', search: '⌕ Søk', crafting: 'Håndverk',
  pump_status: 'PUMPESTATUS', current_power_drains: 'Aktuelle forbrukere',
  fuel_loaded: 'Drivstoff lastet.', no_fuel_stopped: 'INGET DRIVSTOFF — MOTOR STOPPET',
  engine_reset: 'Tilbakestill motor', lox_boost_off: 'LOX-boost: AV',
  throttle: 'gass', actual: 'FAKTISK', target: 'MÅL',
  battery_warning: 'Advarsel: batterinivå for lavt for konfigurert ping!',
  select_fuel: 'Velg en lagret kanister fra Lagring og behandling.',
  change_fuel_heading: 'Bytt Drivstoff', changing_fuel: 'Bytter Drivstoff', close_btn: 'Lukk',
  room_power: 'Romstrøm', auto_off: 'Auto-av: AV',
};

const TRANSLATIONS: Record<Lang, Strings> = { en, es, fr, de, pt, it, ja, ko, zh, nl, ru, pl, sv, tr, ar, no };

const LANG_KEY = 'aegis7_lang';

export const getLanguage = (): Lang => {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored && stored in TRANSLATIONS) return stored as Lang;
    const browserLang = navigator.language.split('-')[0] as Lang;
    if (browserLang in TRANSLATIONS) return browserLang;
  } catch { /* ignore */ }
  return 'en';
};

export const setLanguage = (lang: Lang): void => {
  try { localStorage.setItem(LANG_KEY, lang); } catch { /* ignore */ }
};

export const t = (key: string): string =>
  TRANSLATIONS[getLanguage()]?.[key] ?? TRANSLATIONS.en[key] ?? key;

/**
 * Like t(), but returns '' when the key has no translation in any language.
 * Use for `tOpt(key) || fallback` chains — t()'s raw-key fallback is truthy,
 * so it silently defeats the fallback and the key itself gets displayed
 * (e.g. 'role_facility_shop' shown as a room name).
 */
export const tOpt = (key: string): string =>
  TRANSLATIONS[getLanguage()]?.[key] ?? TRANSLATIONS.en[key] ?? '';
